/**
 * Firebase Auth for the console (docs/06 §1). **Email/password only** in phase 1 — no
 * anonymous sign-in (the CMS is invite-only) and no Google/Apple until `auth.social_enabled`
 * is turned on (decision 2026-09-08).
 *
 * Everything goes through the `CmsAuthClient` interface so screens never import the Firebase
 * SDK and the tests can drive a fake. The real implementation is loaded lazily, keeping the
 * SDK out of the login-screen bundle.
 */
import type { FirebaseClientConfig } from '@akpl/firebase/client';

export interface CmsAuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

export interface CmsAuthClient {
  /** Fires with the restored user (or null) on subscribe and on every change. */
  onAuthState(cb: (user: CmsAuthUser | null) => void): () => void;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  getIdToken(forceRefresh?: boolean): Promise<string | null>;
}

/** Firebase auth error codes mapped to the `common:auth.*` / `common:errors.*` keys. */
export const AUTH_ERROR_KEYS: Record<string, string> = {
  'auth/invalid-email': 'auth.invalidEmail',
  'auth/user-not-found': 'auth.userNotFound',
  'auth/wrong-password': 'auth.wrongPassword',
  'auth/invalid-credential': 'auth.wrongPassword',
  'auth/invalid-login-credentials': 'auth.wrongPassword',
  'auth/too-many-requests': 'auth.tooManyRequests',
  'auth/network-request-failed': 'errors.network_error',
  'auth/user-disabled': 'errors.forbidden',
};

export function authErrorKey(error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code;
  return (typeof code === 'string' && AUTH_ERROR_KEYS[code]) || 'errors.generic';
}

/** Builds the real client over `@akpl/firebase/client` (both modules imported lazily). */
export async function createFirebaseAuthClient(
  config: FirebaseClientConfig,
): Promise<CmsAuthClient> {
  const [{ initFirebase }, { signInWithEmailAndPassword }] = await Promise.all([
    import('@akpl/firebase/client'),
    import('firebase/auth'),
  ]);
  const client = initFirebase(config);

  return {
    onAuthState: (cb) =>
      client.onAuthState((user) =>
        cb(user ? { uid: user.uid, email: user.email, displayName: user.displayName } : null),
      ),
    async signIn(email, password) {
      await signInWithEmailAndPassword(client.auth, email, password);
    },
    signOut: () => client.signOut(),
    getIdToken: (forceRefresh) => client.getIdToken(forceRefresh),
  };
}
